import React, { useState } from "react";
import { ContactApi } from "../lib/api";
import { Mail, Phone, MapPin, ArrowRight, Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [data, setData] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.email || !data.message) { toast.error("Name, email and message are required"); return; }
    setLoading(true);
    try { await ContactApi.send(data); setDone(true); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed to send"); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-white" data-testid="contact-page">
      <section className="container-zelo py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="label-tiny text-brand">Get in Touch</div>
            <h1 className="font-heading text-4xl md:text-6xl font-medium tracking-tight mt-3 text-balance">Talk to the Zelo team.</h1>
            <p className="mt-5 text-lg text-neutral-500 leading-relaxed">Questions about a listing, lease, or your portfolio fit? Our team responds within 24 hours, typically faster.</p>

            <div className="mt-10 space-y-6">
              <div className="rounded-2xl border border-neutral-200 p-5">
                <div className="label-tiny text-brand mb-2">Head Office · Ahmedabad</div>
                <div className="flex gap-3">
                  <MapPin className="w-4 h-4 mt-1 shrink-0 text-neutral-400" />
                  <div className="text-sm text-neutral-700 leading-relaxed">
                    615, 6th Floor, Shivalik Shilp 2,<br />
                    Opp. ITC Narmada, Keshavbaug,<br />
                    Satellite, Ahmedabad, Gujarat 380015.
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-neutral-200 p-5">
                <div className="label-tiny text-brand mb-2">Mumbai Office</div>
                <div className="flex gap-3">
                  <MapPin className="w-4 h-4 mt-1 shrink-0 text-neutral-400" />
                  <div className="text-sm text-neutral-700 leading-relaxed">
                    C-20, G Block Road, G Block BKC,<br />
                    Bandra Kurla Complex, Bandra East,<br />
                    Mumbai, Maharashtra 400051.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-neutral-200 p-5">
                  <div className="label-tiny text-brand mb-2">Speak to</div>
                  <div className="font-heading text-lg font-medium">Mr. Aman Nainani</div>
                  <a href="tel:+918758115155" className="inline-flex items-center gap-2 text-sm text-neutral-700 hover:text-brand mt-1"><Phone className="w-3.5 h-3.5" /> +91 87581 15155</a>
                </div>
                <div className="rounded-2xl border border-neutral-200 p-5">
                  <div className="label-tiny text-brand mb-2">Email</div>
                  <a href="mailto:aman@zelolease.com" className="inline-flex items-center gap-2 text-sm font-medium text-neutral-900 hover:text-brand"><Mail className="w-4 h-4" /> aman@zelolease.com</a>
                  <div className="mt-3"><a href="https://wa.me/918758115155" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-emerald-600"><MessageCircle className="w-4 h-4" /> WhatsApp the team</a></div>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-soft">
            {done ? (
              <div className="text-center py-10" data-testid="contact-success">
                <div className="w-16 h-16 rounded-full bg-brand-50 text-brand mx-auto flex items-center justify-center mb-5"><CheckCircle2 className="w-8 h-8" /></div>
                <div className="font-heading text-2xl font-medium">Message received.</div>
                <div className="text-neutral-500 mt-2">We'll be in touch shortly.</div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input className={inp} placeholder="Name *" value={data.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" />
                  <input type="email" className={inp} placeholder="Email *" value={data.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input className={inp} placeholder="Phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone" />
                  <input className={inp} placeholder="Subject" value={data.subject} onChange={(e) => set("subject", e.target.value)} data-testid="contact-subject" />
                </div>
                <textarea rows={5} className={inp} placeholder="Tell us how we can help… *" value={data.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" />
                <button disabled={loading} className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-3.5 text-sm font-semibold hover:bg-brand-600 transition shadow-brand active:scale-95 disabled:opacity-60" data-testid="contact-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send Message <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const inp = "w-full px-4 py-3 rounded-xl border border-neutral-200 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 text-sm bg-white";
